// The text form: what the source view shows, git holds and a saved file
// is. Markdown for words, tags for components, an id on every node, and
// nothing that runs: a tag's props are literal JSON values.
//
//   ---
//   --accent: oklch(55% 0.17 255)
//   ---
//   # A heading {#12@345}
//   A paragraph with **bold**, _italic_, `code` and [a link](/x). {#13@345}
//   <callout id="14@345" tone="note">
//     Inside it {#15@345 style="color: var(--accent)"}
//   </callout>
//   <counter id="16@345" label="Click me" start={0} />
//
// Marks are toggles, so any overlap of them prints and parses back as it
// was; the characters that mean something are escaped with a backslash.
// meets: source/toggle, history/text
import type { Delta } from "loro-crdt";
import type { NodeJSON, Spec, Target } from "./doc";

export class ParseError extends Error {
  constructor(readonly line: number, message: string) {
    super(`Line ${line}: ${message}`);
  }
}

const TAG = /^[A-Za-z][A-Za-z0-9-]*$/;
const KEY = /^[A-Za-z_][A-Za-z0-9_-]*$/;
const RESERVED = new Set(["id", "style", "type", "props"]);
const ID = /^[0-9]+@[0-9]+$/;

// ---------- printing ----------

export function print(nodes: NodeJSON[], tokens: Record<string, string> = {}): string {
  const out: string[] = [];
  const keys = Object.keys(tokens).sort();
  if (keys.length) out.push("---", ...keys.map((k) => `${k}: ${tokens[k]}`), "---", "");
  const walk = (n: NodeJSON, pad: string) => {
    const level = n.props.level;
    const onlyLevel = Object.keys(n.props).length === 1 && typeof level === "string" && /^[123]$/.test(level);
    const leaf = !n.children.length && n.text !== undefined;
    if (n.type === "heading" && onlyLevel && leaf) return void out.push(pad + "#".repeat(+level!) + " " + inline(n.text!) + suffix(n, n.text!.length > 0));
    if (n.type === "paragraph" && !Object.keys(n.props).length && leaf) {
      const t = inline(n.text!, true);
      return void out.push(pad + t + suffix(n, t.length > 0));
    }
    const [name, attrs] = tag(n);
    if (!n.children.length) {
      if (n.text === undefined) return void out.push(`${pad}<${name}${attrs} />`);
      return void out.push(`${pad}<${name}${attrs}>${inline(n.text)}</${name}>`);
    }
    out.push(`${pad}<${name}${attrs}>`);
    if (n.text !== undefined) out.push(`${pad}  <text>${inline(n.text)}</text>`);
    for (const c of n.children) walk(c, pad + "  ");
    out.push(`${pad}</${name}>`);
  };
  nodes.forEach((n, i) => {
    // A blank line between top-level blocks, as in any markdown.
    if (i) out.push("");
    walk(n, "");
  });
  return out.join("\n") + "\n";
}

const sorted = (o: Record<string, unknown>) => Object.keys(o).sort();
const styleText = (s: Record<string, string>) => sorted(s).map((k) => `${k}: ${s[k]}`).join("; ");

function suffix(n: NodeJSON, space: boolean): string {
  const parts = [`#${n.id}`];
  if (Object.keys(n.style).length) parts.push(`style=${JSON.stringify(styleText(n.style))}`);
  return `${space ? " " : ""}{${parts.join(" ")}}`;
}

function tag(n: NodeJSON): [string, string] {
  const named = TAG.test(n.type) && n.type !== "text" && n.type !== "node";
  let attrs = ` id="${n.id}"`;
  if (!named) attrs += ` type=${JSON.stringify(n.type)}`;
  const keys = sorted(n.props);
  if (keys.every((k) => KEY.test(k) && !RESERVED.has(k))) for (const k of keys) attrs += ` ${k}=${value(n.props[k])}`;
  else attrs += ` props={${JSON.stringify(n.props)}}`;
  if (Object.keys(n.style).length) attrs += ` style=${JSON.stringify(styleText(n.style))}`;
  return [named ? n.type : "node", attrs];
}

const value = (v: unknown) => (typeof v === "string" ? JSON.stringify(v) : `{${JSON.stringify(v)}}`);

// A run of text with its marks, as toggles.
export function inline(delta: Delta<string>[], lineStart = false): string {
  let out = "";
  const on = { bold: false, italic: false, code: false };
  let link: string | undefined;
  const toggles = { bold: "**", italic: "_", code: "`" } as const;
  const closeLink = () => {
    if (link !== undefined) out += `](${link.replace(/[\\)]/g, (c) => "\\" + c)})`;
    link = undefined;
  };
  for (const op of delta) {
    if (!("insert" in op) || !op.insert) continue;
    const a = (op.attributes ?? {}) as Record<string, unknown>;
    const href = typeof a.link === "string" ? a.link : undefined;
    if (href !== link) {
      // Marks don't cross a link's edge in this form: close them, then
      // reopen inside.
      for (const k of ["code", "italic", "bold"] as const) if (on[k]) (out += toggles[k]), (on[k] = false);
      closeLink();
      if (href !== undefined) (out += "["), (link = href);
    }
    for (const k of ["bold", "italic", "code"] as const) if (!!a[k] !== on[k]) (out += toggles[k]), (on[k] = !!a[k]);
    out += escape(op.insert, lineStart && out === "");
  }
  for (const k of ["code", "italic", "bold"] as const) if (on[k]) out += toggles[k];
  closeLink();
  return out;
}

function escape(s: string, atStart: boolean): string {
  let out = s.replace(/[\\*_`[\]{}<]/g, (c) => "\\" + c).replace(/\n/g, "\\n");
  // What would read as a heading, front matter or indentation at a line's start.
  if (atStart && /^[#\- \t]/.test(out)) out = "\\" + out;
  return out;
}

// ---------- parsing ----------

export function parse(src: string): Target {
  const lines = src.replace(/\r\n?/g, "\n").split("\n");
  const tokens: Record<string, string> = {};
  let i = 0;
  while (i < lines.length && !lines[i].trim()) i++;
  if (lines[i]?.trim() === "---") {
    for (i++; i < lines.length && lines[i].trim() !== "---"; i++) {
      const m = lines[i].trim().match(/^(--[a-z][a-z0-9-]*)\s*:\s*(.+)$/);
      if (m) tokens[m[1]] = m[2].trim();
      else if (lines[i].trim()) throw new ParseError(i + 1, "a token is written “--name: value”.");
    }
    if (i >= lines.length) throw new ParseError(i, "the tokens at the top are never closed with “---”.");
    i++;
  }
  const root: Spec[] = [];
  const stack: { spec?: Spec; name: string; line: number; list: Spec[] }[] = [{ name: "", line: 0, list: root }];
  for (; i < lines.length; i++) {
    const n = i + 1;
    const t = lines[i].replace(/^[ \t]+/, "");
    if (!t.trim() || t.startsWith("<!--")) continue;
    const top = stack[stack.length - 1];
    if (t.startsWith("</")) {
      const m = t.trimEnd().match(/^<\/([A-Za-z][A-Za-z0-9-]*)>$/);
      if (!m) throw new ParseError(n, "a closing tag is written “</name>”.");
      if (stack.length === 1 || m[1] !== top.name) throw new ParseError(n, `“</${m[1]}>” closes nothing open${stack.length > 1 ? `; “<${top.name}>” is open` : ""}.`);
      stack.pop();
      continue;
    }
    if (t.startsWith("<")) {
      const m = t.match(/^<([A-Za-z][A-Za-z0-9-]*)/);
      if (!m) throw new ParseError(n, "a tag starts with its component's name, like “<callout”.");
      const name = m[1];
      const { attrs, end, close } = attributes(t, m[0].length, n);
      const rest = t.slice(end);
      if (name === "text") {
        if (!top.spec || close) throw new ParseError(n, "“<text>” holds a container's own words, inside it.");
        const body = rest.trimEnd().match(/^(.*)<\/text>$/);
        if (!body) throw new ParseError(n, "“<text>” ends on its own line with “</text>”.");
        top.spec.delta = parseInline(body[1], n);
        continue;
      }
      const spec = specOf(name, attrs, n);
      top.list.push(spec);
      if (close) {
        if (rest.trim()) throw new ParseError(n, "nothing follows “/>” on its line.");
        continue;
      }
      if (rest.trim() === "") {
        spec.children = [];
        stack.push({ spec, name, line: n, list: spec.children });
        continue;
      }
      const closing = `</${name}>`;
      const body = rest.trimEnd();
      if (!body.endsWith(closing)) throw new ParseError(n, `a tag with words in it ends on its line with “${closing}”.`);
      spec.delta = parseInline(body.slice(0, -closing.length), n);
      continue;
    }
    const h = t.match(/^(#{1,3}) /);
    const body = h ? t.slice(h[0].length) : t;
    const { text, id, style } = splitSuffix(body, n);
    const spec: Spec = h ? { type: "heading", props: { level: String(h[1].length) } } : { type: "paragraph" };
    spec.delta = parseInline(text, n);
    if (id) spec.id = id;
    if (style) spec.style = style;
    top.list.push(spec);
  }
  if (stack.length > 1) {
    const open = stack[stack.length - 1];
    throw new ParseError(open.line, `“<${open.name}>” is never closed.`);
  }
  return { tokens, nodes: root };
}

function specOf(name: string, attrs: Record<string, unknown>, line: number): Spec {
  const spec: Spec = { type: name };
  if (name === "node") {
    if (typeof attrs.type !== "string") throw new ParseError(line, "“<node>” names its component with type=\"…\".");
    spec.type = attrs.type;
  }
  if (attrs.id !== undefined) {
    if (typeof attrs.id !== "string" || !ID.test(attrs.id)) throw new ParseError(line, "an id is written like id=\"12@345\".");
    spec.id = attrs.id;
  }
  if (attrs.style !== undefined) spec.style = styleOf(attrs.style, line);
  let props: Record<string, unknown> = {};
  if (attrs.props !== undefined) {
    if (!attrs.props || typeof attrs.props !== "object" || Array.isArray(attrs.props)) throw new ParseError(line, "props={…} holds an object.");
    props = { ...(attrs.props as object) };
  }
  for (const [k, v] of Object.entries(attrs)) if (!RESERVED.has(k)) props[k] = v;
  spec.props = props;
  return spec;
}

function styleOf(v: unknown, line: number): Record<string, string> {
  if (typeof v !== "string") throw new ParseError(line, "style is written style=\"property: value; …\".");
  const out: Record<string, string> = {};
  for (const part of v.split(";")) {
    if (!part.trim()) continue;
    const at = part.indexOf(":");
    if (at < 1) throw new ParseError(line, `“${part.trim()}” isn't “property: value”.`);
    out[part.slice(0, at).trim()] = part.slice(at + 1).trim();
  }
  return out;
}

// A paragraph's or heading's line: its words, then {#id style="…"} if any.
function splitSuffix(s: string, line: number): { text: string; id?: string; style?: Record<string, string> } {
  let at = -1;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "\\") i++;
    else if (s[i] === "{") {
      at = i;
      break;
    }
  }
  if (at < 0) return { text: s };
  const rest = s.slice(at + 1).trimEnd();
  if (!rest.endsWith("}")) throw new ParseError(line, "a block's “{#id}” ends its line.");
  let inner = rest.slice(0, -1).trim();
  let id: string | undefined;
  const m = inner.match(/^#([0-9]+@[0-9]+)/);
  if (m) (id = m[1]), (inner = inner.slice(m[0].length));
  const { attrs, end } = attributes(inner + "}", 0, line, "}");
  if (end !== inner.length + 1) throw new ParseError(line, "nothing follows a block's “{…}”.");
  for (const k of Object.keys(attrs)) if (k !== "style") throw new ParseError(line, `a paragraph or heading has no “${k}”; use a tag for that.`);
  let text = s.slice(0, at);
  if (text.endsWith(" ")) text = text.slice(0, -1);
  return { text, id, style: attrs.style !== undefined ? styleOf(attrs.style, line) : undefined };
}

// name="json string" or name={json}, up to > or /> (or `stop`).
function attributes(s: string, from: number, line: number, stop = ">"): { attrs: Record<string, unknown>; end: number; close: boolean } {
  const attrs: Record<string, unknown> = {};
  let i = from;
  for (;;) {
    while (s[i] === " " || s[i] === "\t") i++;
    if (i >= s.length) throw new ParseError(line, `a tag ends with “${stop}”.`);
    if (stop === ">" && s.startsWith("/>", i)) return { attrs, end: i + 2, close: true };
    if (s[i] === stop) return { attrs, end: i + 1, close: false };
    const k = s.slice(i).match(/^[A-Za-z_][A-Za-z0-9_-]*/);
    if (!k) throw new ParseError(line, `“${s.slice(i, i + 12)}” isn't name="value".`);
    i += k[0].length;
    if (s[i] !== "=") throw new ParseError(line, `“${k[0]}” needs a value: ${k[0]}="…" or ${k[0]}={…}.`);
    i++;
    let raw: string;
    if (s[i] === '"') {
      let j = i + 1;
      while (j < s.length && s[j] !== '"') j += s[j] === "\\" ? 2 : 1;
      raw = s.slice(i, j + 1);
      i = j + 1;
    } else if (s[i] === "{") {
      let depth = 0;
      let j = i;
      for (; j < s.length; j++) {
        if (s[j] === '"') {
          for (j++; j < s.length && s[j] !== '"'; j++) if (s[j] === "\\") j++;
        } else if (s[j] === "{") depth++;
        else if (s[j] === "}" && --depth === 0) break;
      }
      raw = s.slice(i + 1, j);
      i = j + 1;
    } else throw new ParseError(line, `${k[0]}'s value is "text" or {json}.`);
    try {
      attrs[k[0]] = JSON.parse(raw);
    } catch {
      throw new ParseError(line, `${k[0]}'s value isn't a literal: ${raw.slice(0, 40)}`);
    }
  }
}

export function parseInline(s: string, line = 0): Delta<string>[] {
  type Run = { text: string; attrs: Record<string, unknown> };
  const runs: Run[] = [];
  const on: Record<string, unknown> = {};
  let linkFrom = -1;
  const PENDING = "\u0000link";
  const push = (ch: string) => {
    const attrs = { ...on };
    const last = runs[runs.length - 1];
    if (last && JSON.stringify(last.attrs) === JSON.stringify(attrs)) last.text += ch;
    else runs.push({ text: ch, attrs });
  };
  const toggle = (k: string) => (on[k] ? delete on[k] : (on[k] = true));
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === "\\" && i + 1 < s.length) {
      const next = s[++i];
      push(next === "n" ? "\n" : next);
    } else if (c === "*" && s[i + 1] === "*") toggle("bold"), i++;
    else if (c === "_") toggle("italic");
    else if (c === "`") toggle("code");
    else if (c === "[") {
      if (linkFrom >= 0) throw new ParseError(line, "a link can't hold another link.");
      linkFrom = runs.length;
      on.link = PENDING;
    } else if (c === "]") {
      if (linkFrom < 0 || s[i + 1] !== "(") throw new ParseError(line, "“]” closes a link, as [words](address).");
      let j = i + 2;
      let href = "";
      for (; j < s.length && s[j] !== ")"; j++) href += s[j] === "\\" ? s[++j] ?? "" : s[j];
      if (j >= s.length) throw new ParseError(line, "a link's address ends with “)”.");
      // The link's runs were pushed with the placeholder; give them the address.
      for (let r = linkFrom; r < runs.length; r++) if (runs[r].attrs.link === PENDING) runs[r].attrs = { ...runs[r].attrs, link: href };
      delete on.link;
      linkFrom = -1;
      i = j;
    } else push(c);
  }
  if (linkFrom >= 0) throw new ParseError(line, "a link is opened with “[” and never closed.");
  // Runs that ended up the same once their link was known become one.
  const out: Delta<string>[] = [];
  for (const r of runs) {
    const prev = out[out.length - 1] as { insert: string; attributes?: Record<string, unknown> } | undefined;
    const attributes = Object.keys(r.attrs).length ? r.attrs : undefined;
    if (prev && JSON.stringify(prev.attributes) === JSON.stringify(attributes)) prev.insert += r.text;
    else out.push((attributes ? { insert: r.text, attributes } : { insert: r.text }) as Delta<string>);
  }
  return out;
}
